import { Alert, Box, Stack } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import ScrollTable from 'components/global/CUK/ScrollTable'
import TableSkeleton from 'components/global/CUK/Skeletons/TableSkeleton'
import EmptyScreen from 'packages/ui/EmptyScreen'
import TableView, { type IItem, useTable } from 'packages/ui/Table'

import { type DeliverySource, type DeliverySourceType } from '../../types'
import CountBadge from '../CountBadge'
import COLUMNS_CONFIG from './columns-config'
import useDeliverySources from './use-delivery-sources'

type SourcesTableProps = {
  sourceType: DeliverySourceType
  onSelect: (source: DeliverySource) => void
}

const SourcesTable = ({ sourceType, onSelect }: SourcesTableProps) => {
  const { t } = useTranslation()
  const deliverySources = useDeliverySources({ sourceType })
  const apiTable = useTable({
    rowKey: 'id',
    loading: deliverySources.loading,
    loadingNext: deliverySources.loadingNext,
    items: deliverySources.items as unknown as IItem[],
    columns: COLUMNS_CONFIG,
    onRowClick: (item) => {
      onSelect(item as unknown as DeliverySource)
    }
  })

  const renderedTable = useMemo(() => {
    if (deliverySources.loading) {
      return <TableSkeleton />
    }

    if (deliverySources.error) {
      return (
        <Stack justifyContent="center" sx={{ p: 2 }}>
          <Alert severity="error" sx={{ borderRadius: '16px' }}>
            {t('mailing.history.error_list')}
          </Alert>
        </Stack>
      )
    }

    if (!deliverySources.items.length) {
      return (
        <EmptyScreen icon="envelope" variant="compact">
          {sourceType === 'campaign' ? t('mailing.history.list_none_campaign') : t('mailing.history.list_none_trigger')}
        </EmptyScreen>
      )
    }

    return <TableView apiTable={apiTable} />
  }, [
    apiTable,
    deliverySources.error,
    deliverySources.items.length,
    deliverySources.loading,
    sourceType
  ])

  return (
    <Stack gap={2} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
        <CountBadge
          count={deliverySources.total}
          singularLabel="élément"
          pluralLabel="éléments"
        />
      </Stack>

      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
        <ScrollTable
          height="100%"
          fetchNextPage={deliverySources.fetchNextPage}
          shouldFetchNextPage={deliverySources.shouldFetchNextPage}
          style={{ borderRadius: '8px' }}
        >
          {renderedTable}
        </ScrollTable>
      </Box>
    </Stack>
  )
}

export default SourcesTable
